import { create } from 'zustand';
import config from '../config';
import useSessionStore from './session';
import useAppStore from './app';
import localSessionData from './session-dummy-data';
import { NonV2RoomError } from '../helpers/errors';
import {
  CLIENT_TYPES,
  PROCTOR_ROOM_EVENTS,
  SyncClient,
} from '@smarterservices/sync-client';

const useDataBroker = create((set, get) => ({
  mode: 'remote',
  sync: {
    client: null,
    doc: null,
    emitter: null,
  },

  initialize: async (mode) => {
    if (mode === 'remote') {
      // load data from the sync doc...

      const syncClientConfig = {
        apiUrl: config.proctoring.api.baseUrl,
        documentId: `Proctor_Room_${useAppStore.getState().roomSid}`,
        identity: `${useAppStore.getState().user.sid}${useAppStore.getState().roomSid}`,
        token: useAppStore.getState().user.token,
        type: CLIENT_TYPES.PROCTOR_ROOM,
      };

      //console.log(`Sync Client Config`, syncClientConfig);

      const localSync = { ...get().sync };

      localSync.client = await SyncClient(syncClientConfig);

      localSync.doc = await localSync.client.getSync();

      if (!localSync.doc.sessions) {
        // This is a v1 room, which we cannot join. null the sync client
        // so the user is not kept as an active proctor in the room
        throw new NonV2RoomError();
      }

      // attach the event emitter to the store...
      localSync.emitter = localSync.client.getEventEmitter();

      // set in state...
      set({ sync: localSync });

      // register the listeners
      get().setupListeners();

      // register the user as in the room, but remove the token first as this should not be exposed to others...
      const userData = { ...useAppStore.getState().user };
      delete userData.token;
      await get().proctorUserEnter(useAppStore.getState().user.sid, userData);

      // now call to load all the sessions...
      Object.entries(localSync.doc.sessions).forEach(([sessionSid, data]) => {
        useSessionStore.getState().add(sessionSid, data);
      });

      // now add all the proctors that are in teh room.
      Object.entries(localSync.doc.proctors).forEach(
        ([proctorAccountUserSid, data]) => {
          useAppStore.getState().addProctor(proctorAccountUserSid, data);
        }
      );
    } else {
      // eslint-disable-next-line no-unused-vars
      Object.entries(localSessionData).forEach(([sessionSid, data]) => {
        //useSessionStore.getState().add(sessionSid, data);
      });
    }

    return {
      successful: true,
      mode,
    };
  },

  setDataMode: (mode) => {
    set({ mode });
  },

  /**
   * Persists a session update to the sync document.
   *
   * @param {string} sessionSid - The session ID of the session to update.
   * @param {Object} data - The data associated with the session to update.
   * @returns {Promise} - Resolves with the result of the sync operation.
   */
  persist: async (sessionSid, data) => {
    const dataToUpdate = {
      sessions: {
        [sessionSid]: data,
      },
    };

    const result = await get().sync.client.setSync(dataToUpdate);
    return result;
  },

  /**
   * Registers a proctor user in the proctor room document.
   *
   * This function updates the sync document by adding or updating a proctor
   * user's data, identified by their unique proctor user SID.
   *
   * @param {string} proctorUserSid - The unique identifier for the proctor user.
   * @param {object} data - The data associated with the proctor user to be stored.
   * @returns {Promise} - Resolves with the result of the sync operation.
   */

  proctorUserEnter: async (proctorUserSid, data) => {
    const dataToUpdate = {
      proctors: {
        [proctorUserSid]: data,
      },
    };
    const result = await get().sync.client.setSync(dataToUpdate);
    return result;
  },

  /**
   * Remove a field from the proctor room document. This is a promise
   * that resolves with the result of the delete operation.
   * @param {string} path - The dotted path of the field to remove.
   * @returns {Promise} - Resolves with the result of the delete operation.
   */
  remove: async (path) => {
    const result = await get().sync.client.deleteFieldByDottedPath(path);
    return result;
  },

  /**
   * Sets up event listeners for various proctor room events.
   *
   * This function initializes listeners on the sync emitter for handling
   * different student-related events within the proctor room. It listens
   * to events such as when a student enters, transitions between states,
   * or exits the room, and updates the session store accordingly.
   *
   * Specifically, it handles the following events:
   * - STUDENT_ENTERED: Adds the student session to the store when a student joins the room.
   * - STUDENT_TRANSITIONED: Updates the student's location in the store when a student transitions states.
   * - STUDENT_EXITED: Removes the student's session from the store when a student exits the room.
   */

  setupListeners: () => {
    // Student Joins room...
    get().sync.emitter.on(
      PROCTOR_ROOM_EVENTS.STUDENT_ENTERED,
      function (student) {
        useSessionStore.getState().add(student.sessionSid, student.data);
      }
    );

    // student moved state...
    get().sync.emitter.on(
      PROCTOR_ROOM_EVENTS.STUDENT_TRANSITIONED,
      function (student) {
        useSessionStore
          .getState()
          .handleTransitionEvent(student.sessionSid, student.data.location);
      }
    );

    // student exited room...
    get().sync.emitter.on(
      PROCTOR_ROOM_EVENTS.STUDENT_EXITED,
      function (student) {
        useSessionStore.getState().handleRemoveEvent(student.sessionSid);
      }
    );

    // Proctor exited the room...
    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.PROCTOR_EXITED, function (event) {
      // if the proctor that exited was the currently logged in, log them out...
      if (
        event.proctorUserSid === useAppStore.getState().user.sid &&
        useAppStore.getState().room
      ) {
        useAppStore.getState().logout({
          loginScreenMessage:
            'You were automatically logged out. Please log in again.',
        });
      } else {
        useAppStore.getState().removeProctor(event.proctorUserSid);
      }
    });

    // proctor entered room...
    get().sync.emitter.on(
      PROCTOR_ROOM_EVENTS.PROCTOR_ENTERED,
      function (event) {
        useAppStore.getState().addProctor(event.proctorUserSid, event.data);
      }
    );

    // Proctor data updated...
    get().sync.emitter.on(
      PROCTOR_ROOM_EVENTS.PROCTOR_UPDATED,
      function (event) {
        useAppStore.getState().addProctor(event.proctorUserSid, event.data);
      }
    );
    /*    
      get().sync.emitter.on(
        PROCTOR_ROOM_EVENTS.SYNC_DOC_UPDATED_WITH_PREVIOUS_DATA,
        function (data) {
            console.log(data);
        }
      );
      */
  },
}));

export default useDataBroker;
